/*
 * Soft tints for a person's (or a named record's) initials, picked from the name so it keeps its colour across
 * pages, reloads and surfaces (table cell, hover card, popover, dialog). No orange (the logo's) and no plain green
 * (the app's actions), so the tint never reads as a button or a status.
 */
export enum IdentityTint {
  Sky = "sky",
  Violet = "violet",
  Teal = "teal",
  Rose = "rose",
  Indigo = "indigo",
  Cyan = "cyan",
}

/** Order matters: the hash picks by index, so appending is safe but reordering recolours everyone. */
const IDENTITY_TINT_ORDER: IdentityTint[] = [
  IdentityTint.Sky,
  IdentityTint.Violet,
  IdentityTint.Teal,
  IdentityTint.Rose,
  IdentityTint.Indigo,
  IdentityTint.Cyan,
]

/** Initials avatar / mark: soft tint background, deep tint text. */
const INITIALS_CLASS: Record<IdentityTint, string> = {
  [IdentityTint.Sky]: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300",
  [IdentityTint.Violet]: "bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300",
  [IdentityTint.Teal]: "bg-teal-100 text-teal-800 dark:bg-teal-500/15 dark:text-teal-300",
  [IdentityTint.Rose]: "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300",
  [IdentityTint.Indigo]: "bg-indigo-100 text-indigo-800 dark:bg-indigo-500/15 dark:text-indigo-300",
  [IdentityTint.Cyan]: "bg-cyan-100 text-cyan-800 dark:bg-cyan-500/15 dark:text-cyan-300",
}

/** Identity card header: the tint fading to the card colour, left to right (low alpha on the card in dark mode). */
const HEADER_CLASS: Record<IdentityTint, string> = {
  [IdentityTint.Sky]: "bg-linear-to-r from-sky-100 to-card dark:from-sky-500/15",
  [IdentityTint.Violet]: "bg-linear-to-r from-violet-100 to-card dark:from-violet-500/15",
  [IdentityTint.Teal]: "bg-linear-to-r from-teal-100 to-card dark:from-teal-500/15",
  [IdentityTint.Rose]: "bg-linear-to-r from-rose-100 to-card dark:from-rose-500/15",
  [IdentityTint.Indigo]: "bg-linear-to-r from-indigo-100 to-card dark:from-indigo-500/15",
  [IdentityTint.Cyan]: "bg-linear-to-r from-cyan-100 to-card dark:from-cyan-500/15",
}

/** The deterministic tint for a name (same name, same tint, everywhere). */
export function getIdentityTint(name: string | null | undefined): IdentityTint {
  let hash = 0
  for (const char of name ?? "") hash = (hash * 31 + char.charCodeAt(0)) | 0
  return IDENTITY_TINT_ORDER[Math.abs(hash) % IDENTITY_TINT_ORDER.length]
}

/** Classes for the tinted initials avatar or mark of a name. */
export function identityInitialsTint(name: string | null | undefined): string {
  return INITIALS_CLASS[getIdentityTint(name)]
}

/** Classes for the identity card's tinted header (gradient from the name's tint to the card colour). */
export function identityHeaderTint(name: string | null | undefined): string {
  return HEADER_CLASS[getIdentityTint(name)]
}
